import { useState, useCallback, useEffect, useRef } from '@wordpress/element';
import { __ } from '@wordpress/i18n';
import apiFetch from '@wordpress/api-fetch';
import { Popover } from '@wordpress/components';
import { ChevronLeft, ChevronDown, X } from 'lucide-react';
import RuleTitle from '../components/quote-rules/RuleTitle';
import ApplyForRoles from '../components/quote-rules/ApplyForRoles';
import RulePriority from '../components/quote-rules/RulePriority';
import StatusPill from '../components/quote-rule-list/StatusPill';
import { addQueryArgs } from '@wordpress/url';
import {
    ProductAsyncSelect,
    VendorAsyncSelect,
    DokanButton,
} from '@dokan/components';
import {
    SimpleInput,
    ToggleSwitch,
    SimpleCheckbox,
    SimpleRadio,
} from '@getdokan/dokan-ui';
import { twMerge } from 'tailwind-merge';

type QuoteRule = {
    rule_name: string;
    store_ids: Array< { id: number; name: string; image?: string } >;
    product_ids: Array< { id: number; name: string; image?: string } >;
    category_ids: number[];
    hide_price: number;
    expire_limit: number;
    hide_price_text: string;
    button_text: string;
    rule_priority: number;
    hide_cart_button: 'replace' | 'keep_and_add_new';
    exclude_store_ids: Array< { id: number; name: string; image?: string } >;
    selected_user_role: string[];
    exclude_product_ids: Array< { id: number; name: string; image?: string } >;
    apply_on_all_product: number;
    switches: {
        vendor_switch: boolean;
        expire_switch: boolean;
        product_switch: boolean;
        category_switch: boolean;
    };
};

type Category = {
    id: number;
    name: string;
    hasChildren: boolean;
    children?: Category[];
};

const AddNewQuoteRule = ( { params } ) => {
    const id = parseInt( params?.id || 0 );
    const isEditMode = !! id;

    const [ rule, setRule ] = useState< QuoteRule >( {
        rule_name: '',
        store_ids: [],
        product_ids: [],
        category_ids: [],
        hide_price: 1,
        expire_limit: 30,
        hide_price_text: __( 'Price is hidden', 'dokan' ),
        button_text: __( 'Add to quote', 'dokan' ),
        rule_priority: 0,
        hide_cart_button: 'replace',
        exclude_store_ids: [],
        selected_user_role: [],
        exclude_product_ids: [],
        apply_on_all_product: 0,
        switches: {
            vendor_switch: false,
            expire_switch: false,
            product_switch: false,
            category_switch: false,
        },
    } );

    const [ allRoles, setAllRoles ] = useState< Record< string, string > >(
        {}
    );
    const [ categories, setCategories ] = useState< Category[] >( [] );
    const [ isSubmitting, setIsSubmitting ] = useState( false );
    const [ isLoading, setIsLoading ] = useState( isEditMode );
    const [ errors, setErrors ] = useState< string[] >( [] );
    const [ ruleStatus, setRuleStatus ] = useState( 'draft' );
    const [ isDropdownOpen, setIsDropdownOpen ] = useState( false );
    const dropdownAnchorRef = useRef< HTMLButtonElement >( null );
    const [ showProductsDropdown, setShowProductsDropdown ] = useState( true );

    // Fetch roles
    useEffect( () => {
        apiFetch( { path: '/dokan/v1/request-for-quote/roles' } )
            .then( ( response: any ) => {
                const roles = {
                    ...response.role_names,
                    dokan_wholesale_customer: __(
                        'Wholesale customer',
                        'dokan'
                    ),
                    guest: __( 'Guest', 'dokan' ),
                };
                setAllRoles( roles );
            } )
            .catch( console.error );
    }, [] );

    // Fetch categories
    useEffect( () => {
        apiFetch( { path: '/dokan/v1/products/multistep-categories' } )
            .then( ( response: any ) => {
                const cats: Category[] = [];
                Object.values( response ).forEach( ( item: any ) => {
                    if ( item.parent_id != 0 ) return;
                    const catData: Category = {
                        id: Number( item.term_id ),
                        name: item.name,
                        hasChildren: false,
                    };
                    if ( item.children?.length > 0 ) {
                        catData.hasChildren = true;
                        catData.children = getChildCategories(
                            response,
                            item.children
                        );
                    }
                    cats.push( catData );
                } );
                setCategories( cats );
            } )
            .catch( console.error );
    }, [] );

    // Fetch rule data if editing
    useEffect( () => {
        if ( isEditMode && id ) {
            apiFetch( {
                path: `/dokan/v1/request-for-quote/quote-rule/${ id }`,
            } )
                .then( ( response: any ) => {
                    setIsLoading( false );
                    setRule( {
                        ...response,
                        hide_price: parseInt( response.hide_price ),
                        apply_on_all_product: parseInt(
                            response.apply_on_all_product
                        ),
                        product_ids:
                            response.product_ids?.map( ( p: any ) => ( {
                                id: p.id,
                                name: p.name,
                                image: p.image || '',
                            } ) ) || [],
                        exclude_product_ids:
                            response.exclude_product_ids?.map( ( p: any ) => ( {
                                id: p.id,
                                name: p.name,
                                image: p.image || '',
                            } ) ) || [],
                        store_ids:
                            response.store_ids?.map( ( store: any ) => ( {
                                id: store.store_id,
                                name: store.store_name,
                                image: store.store_image || '',
                            } ) ) || [],
                        exclude_store_ids:
                            response.exclude_store_ids?.map(
                                ( store: any ) => ( {
                                    id: store.store_id,
                                    name: store.store_name,
                                    image: store.store_image || '',
                                } )
                            ) || [],
                        switches: {
                            vendor_switch:
                                response.switches?.vendor_switch === 'true' ||
                                response.switches?.vendor_switch === true,
                            expire_switch:
                                response.switches?.expire_switch === 'true' ||
                                response.switches?.expire_switch === true,
                            product_switch:
                                response.switches?.product_switch === 'true' ||
                                response.switches?.product_switch === true,
                            category_switch:
                                response.switches?.category_switch === 'true' ||
                                response.switches?.category_switch === true,
                        },
                    } );
                    setShowProductsDropdown(
                        ! parseInt( response.apply_on_all_product )
                    );
                    if ( response.status ) {
                        setRuleStatus( response.status );
                    }
                } )
                .catch( ( err ) => {
                    console.error( err );
                    setIsLoading( false );
                } );
        }
    }, [ isEditMode, id ] );

    const getChildCategories = ( data: any, children: any[] ): Category[] => {
        return children.map( ( item ) => {
            const childCategory: Category = {
                id: Number( data[ item ].term_id ),
                name: data[ item ].name,
                hasChildren: false,
            };
            if ( data[ item ].children?.length > 0 ) {
                childCategory.hasChildren = true;
                childCategory.children = getChildCategories(
                    data,
                    data[ item ].children
                );
            }
            return childCategory;
        } );
    };

    const handleRoleChange = useCallback( ( roleKey: string ) => {
        setRule( ( prev ) => ( {
            ...prev,
            selected_user_role: prev.selected_user_role.includes( roleKey )
                ? prev.selected_user_role.filter( ( r ) => r !== roleKey )
                : [ ...prev.selected_user_role, roleKey ],
        } ) );
    }, [] );

    const handleCategoryChange = useCallback( ( categoryId: number ) => {
        setRule( ( prev ) => ( {
            ...prev,
            category_ids: prev.category_ids.includes( categoryId )
                ? prev.category_ids.filter( ( c ) => c !== categoryId )
                : [ ...prev.category_ids, categoryId ],
        } ) );
    }, [] );

    const renderCategoryTree = ( cats: Category[], depth = 0 ) => {
        return cats.map( ( cat ) => (
            <div
                className="space-y-5"
                key={ cat.id }
                style={ { marginLeft: `${ depth * 28 }px` } }
            >
                <label className="inline-flex items-center gap-1 py-1">
                    <SimpleCheckbox
                        className="m-0 focus:ring-0 w-[18px] h-[18px]"
                        checked={ rule.category_ids.includes( cat.id ) }
                        onChange={ () => handleCategoryChange( cat.id ) }
                        input={ {} }
                    />
                    <span className="text-sm text-[#575757]">{ cat.name }</span>
                </label>
                { cat.children &&
                    renderCategoryTree( cat.children, depth + 1 ) }
            </div>
        ) );
    };

    const validateForm = (): boolean => {
        const errs: string[] = [];

        if ( ! rule.rule_name.trim() ) {
            errs.push( __( 'Please enter rule name.', 'dokan' ) );
        }

        if ( rule.selected_user_role.length === 0 ) {
            errs.push( __( 'Please select at least one user role.', 'dokan' ) );
        }

        if ( ! rule.hide_price_text.trim() ) {
            errs.push( __( 'Please enter hide price text.', 'dokan' ) );
        }

        if ( ! rule.button_text.trim() ) {
            errs.push( __( 'Please enter button text.', 'dokan' ) );
        }

        if ( rule.switches.expire_switch ) {
            const expireLimit = rule.expire_limit;
            if (
                isNaN( expireLimit ) ||
                expireLimit < 1 ||
                ! /^[1-9]\d*$/.test( String( expireLimit ) )
            ) {
                errs.push(
                    __(
                        'Please enter a valid positive integer for quote expiration days limit.',
                        'dokan'
                    )
                );
            }
        }

        setErrors( errs );
        return errs.length === 0;
    };

    const handleSubmit = useCallback(
        async ( status: 'publish' | 'draft' ) => {
            if ( ! validateForm() ) return;

            setIsSubmitting( true );

            const payload: any = {
                ...rule,
                status,
                hide_price: rule.hide_price ? '1' : '0',
                apply_on_all_product: rule.apply_on_all_product ? '1' : '0',
                switches: {
                    vendor_switch: rule.switches.vendor_switch
                        ? 'true'
                        : 'false',
                    expire_switch: rule.switches.expire_switch
                        ? 'true'
                        : 'false',
                    product_switch: rule.switches.product_switch
                        ? 'true'
                        : 'false',
                    category_switch: rule.switches.category_switch
                        ? 'true'
                        : 'false',
                },
                product_ids: ! rule.apply_on_all_product
                    ? rule.product_ids.map( ( p ) => p.id )
                    : [],
                exclude_product_ids: ! rule.apply_on_all_product
                    ? rule.exclude_product_ids.map( ( p ) => p.id )
                    : [],
                store_ids: ! rule.apply_on_all_product
                    ? rule.store_ids.map( ( s ) => s.id )
                    : [],
                exclude_store_ids: ! rule.apply_on_all_product
                    ? rule.exclude_store_ids.map( ( s ) => s.id )
                    : [],
                category_ids: ! rule.apply_on_all_product
                    ? rule.category_ids
                    : [],
            };

            try {
                if ( isEditMode && id ) {
                    await apiFetch( {
                        path: `/dokan/v1/request-for-quote/quote-rule/${ id }`,
                        method: 'PUT',
                        data: payload,
                    } );
                } else {
                    await apiFetch( {
                        path: '/dokan/v1/request-for-quote/quote-rule',
                        method: 'POST',
                        data: payload,
                    } );
                }
                window.location.hash = '#/request-for-quote/quote-rules';
            } catch ( e: any ) {
                const msg =
                    e?.message || __( 'Failed to save quote rule.', 'dokan' );
                setErrors( ( prev ) => [ ...prev, msg ] );
            } finally {
                setIsSubmitting( false );
            }
        },
        [ rule, isEditMode, id ]
    );

    const handleProductSelect = useCallback( ( product: any ) => {
        if ( product ) {
            const newProduct = {
                id: product.value,
                name: product.label,
                image: product?.raw?.images?.[ 0 ]?.src || '',
            };

            setRule( ( prev ) => {
                // prevent duplicate in include
                const alreadyIncluded = prev.product_ids.some(
                    ( p ) => String( p.id ) === String( newProduct.id )
                );
                const filteredExcluded = prev.exclude_product_ids.filter(
                    ( p ) => String( p.id ) !== String( newProduct.id )
                );
                return {
                    ...prev,
                    product_ids: alreadyIncluded
                        ? prev.product_ids
                        : [ ...prev.product_ids, newProduct ],
                    exclude_product_ids: filteredExcluded,
                };
            } );
        }
    }, [] );

    const handleExcludeProductSelect = useCallback( ( product: any ) => {
        if ( product ) {
            const newProduct = {
                id: product.value,
                name: product.label,
                image: product?.raw?.images?.[ 0 ]?.src || '',
            };
            setRule( ( prev ) => {
                // prevent duplicate in exclude
                const alreadyExcluded = prev.exclude_product_ids.some(
                    ( p ) => String( p.id ) === String( newProduct.id )
                );
                const filteredIncluded = prev.product_ids.filter(
                    ( p ) => String( p.id ) !== String( newProduct.id )
                );
                return {
                    ...prev,
                    exclude_product_ids: alreadyExcluded
                        ? prev.exclude_product_ids
                        : [ ...prev.exclude_product_ids, newProduct ],
                    product_ids: filteredIncluded,
                };
            } );
        }
    }, [] );

    // Build loadOptions for product selects to exclude items already chosen in the opposite list
    const loadIncludeProductOptions = useCallback(
        async ( inputValue: string ) => {
            try {
                const data: any[] = await apiFetch( {
                    path: addQueryArgs( '/dokan/v1/products', {
                        search: inputValue || '',
                        per_page: 20,
                    } ),
                } );
                const excludeIds = new Set(
                    rule.exclude_product_ids.map( ( p ) => String( p.id ) )
                );
                const selfIds = new Set(
                    rule.product_ids.map( ( p ) => String( p.id ) )
                );
                return data
                    .filter( ( product: any ) => {
                        const id = String( product.id );
                        // hide if present in exclude list or already included
                        return ! excludeIds.has( id ) && ! selfIds.has( id );
                    } )
                    .map( ( product: any ) => ( {
                        value: product.id,
                        label: product.name || `Product #${ product.id }`,
                        raw: product,
                    } ) );
            } catch ( e ) {
                return [] as any[];
            }
        },
        [ rule.exclude_product_ids, rule.product_ids ]
    );

    const loadExcludeProductOptions = useCallback(
        async ( inputValue: string ) => {
            try {
                const data: any[] = await apiFetch( {
                    path: addQueryArgs( '/dokan/v1/products', {
                        search: inputValue || '',
                        per_page: 20,
                    } ),
                } );
                const includeIds = new Set(
                    rule.product_ids.map( ( p ) => String( p.id ) )
                );
                const selfIds = new Set(
                    rule.exclude_product_ids.map( ( p ) => String( p.id ) )
                );
                return data
                    .filter( ( product: any ) => {
                        const id = String( product.id );
                        // hide if present in include list or already excluded
                        return ! includeIds.has( id ) && ! selfIds.has( id );
                    } )
                    .map( ( product: any ) => ( {
                        value: product.id,
                        label: product.name || `Product #${ product.id }`,
                        raw: product,
                    } ) );
            } catch ( e ) {
                return [] as any[];
            }
        },
        [ rule.product_ids, rule.exclude_product_ids ]
    );

    const handleVendorSelect = useCallback( ( vendor: any ) => {
        if ( vendor ) {
            const newVendor = {
                id: vendor.value,
                name: vendor.label,
                image: vendor?.raw?.gravatar || '',
            };
            setRule( ( prev ) => {
                const alreadyIncluded = prev.store_ids.some(
                    ( s ) => String( s.id ) === String( newVendor.id )
                );
                const filteredExcluded = prev.exclude_store_ids.filter(
                    ( s ) => String( s.id ) !== String( newVendor.id )
                );
                return {
                    ...prev,
                    store_ids: alreadyIncluded
                        ? prev.store_ids
                        : [ ...prev.store_ids, newVendor ],
                    exclude_store_ids: filteredExcluded,
                };
            } );
        }
    }, [] );

    const handleExcludeVendorSelect = useCallback( ( vendor: any ) => {
        if ( vendor ) {
            const newVendor = {
                id: vendor.value,
                name: vendor.label,
                image: vendor?.raw?.gravatar || '',
            };
            setRule( ( prev ) => {
                const alreadyExcluded = prev.exclude_store_ids.some(
                    ( s ) => String( s.id ) === String( newVendor.id )
                );
                const filteredIncluded = prev.store_ids.filter(
                    ( s ) => String( s.id ) !== String( newVendor.id )
                );
                return {
                    ...prev,
                    exclude_store_ids: alreadyExcluded
                        ? prev.exclude_store_ids
                        : [ ...prev.exclude_store_ids, newVendor ],
                    store_ids: filteredIncluded,
                };
            } );
        }
    }, [] );

    return (
        <>
            { /* Header */ }
            <div className="flex items-center justify-between mb-4">
                <div>
                    <a
                        href="#/request-for-quote/quote-rules"
                        className="flex items-center gap-1 text-sm text-[#828282] hover:text-[#7047EB] mb-2"
                    >
                        <ChevronLeft size={ 16 } />
                        { __( 'Quote Rules', 'dokan' ) }
                    </a>
                    <h1 className="text-2xl font-semibold text-[#25252D] pl-1 flex items-center gap-5">
                        { isEditMode
                            ? __( 'Editing Rule', 'dokan' )
                            : __( 'Add New Rules', 'dokan' ) }
                        <StatusPill
                            value={ ruleStatus }
                            editMode={ isEditMode }
                        />
                    </h1>
                </div>
                <div className="flex gap-2 items-center">
                    <div className="relative inline-flex">
                        <DokanButton
                            type="primary"
                            className="px-5 py-2.5 rounded-md !rounded-r-none shadow-none"
                            onClick={ () => handleSubmit( 'publish' ) }
                            disabled={ isSubmitting }
                        >
                            { __( 'Publish', 'dokan' ) }
                        </DokanButton>
                        <hr className="h-full w-[1px] border-l border-white" />
                        <DokanButton
                            type="primary"
                            className="px-3 py-2.5 rounded-md !rounded-l-none shadow-none"
                            ref={ dropdownAnchorRef }
                            onClick={ () =>
                                setIsDropdownOpen( ! isDropdownOpen )
                            }
                            disabled={ isSubmitting }
                        >
                            <ChevronDown size={ 16 } strokeWidth={ 3 } />
                        </DokanButton>
                        { isDropdownOpen && (
                            <Popover
                                anchor={ dropdownAnchorRef.current }
                                onClose={ () => setIsDropdownOpen( false ) }
                                onFocusOutside={ () =>
                                    setIsDropdownOpen( false )
                                }
                                className="dokan-layout"
                            >
                                <div className="header-popover bg-white rounded-md shadow-md min-w-[200px] transition-all duration-200 ease-in-out py-2">
                                    <ul className="flex flex-col p-0 m-0 list-none">
                                        <li className="m-0">
                                            <button
                                                className="w-full rounded-none hover:bg-[#EFEAFF] text-[#828282] hover:text-[#7047EB] px-4 py-2 text-left"
                                                onClick={ () => {
                                                    handleSubmit( 'draft' );
                                                    setIsDropdownOpen( false );
                                                } }
                                                disabled={ isSubmitting }
                                            >
                                                { __(
                                                    'Save as Draft',
                                                    'dokan'
                                                ) }
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </Popover>
                        ) }
                    </div>
                </div>
            </div>

            { /* Error Messages */ }
            { errors.length > 0 && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded text-red-700">
                    <ul className="list-disc list-inside text-sm">
                        { errors.map( ( err, i ) => (
                            <li key={ i }>{ err }</li>
                        ) ) }
                    </ul>
                </div>
            ) }

            { /* Loading Skeleton */ }
            { isLoading && (
                <div className="flex gap-5 animate-pulse">
                    <div className="flex-1 space-y-5">
                        { /* Rule Title Skeleton */ }
                        <div className="bg-white rounded-lg shadow p-7">
                            <div className="h-4 bg-gray-200 rounded w-24 mb-4" />
                            <div className="h-10 bg-gray-200 rounded w-full" />
                        </div>

                        { /* Rule Setting Skeleton */ }
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-7 py-5 border-b border-gray-200">
                                <div className="h-5 bg-gray-200 rounded w-32" />
                            </div>
                            { [ 1, 2, 3, 4, 5 ].map( ( i ) => (
                                <div
                                    key={ i }
                                    className="px-7 py-5 border-b border-gray-200 flex items-center justify-between"
                                >
                                    <div className="space-y-2">
                                        <div className="h-4 bg-gray-200 rounded w-36" />
                                        <div className="h-3 bg-gray-200 rounded w-56" />
                                    </div>
                                    <div className="h-6 w-11 bg-gray-200 rounded-full" />
                                </div>
                            ) ) }
                        </div>

                        { /* Cart Button Customization Skeleton */ }
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-7 py-5 border-b border-gray-200">
                                <div className="h-5 bg-gray-200 rounded w-64" />
                            </div>
                            <div className="px-7 py-5 border-b border-gray-200 space-y-3">
                                <div className="h-4 bg-gray-200 rounded w-52" />
                                <div className="h-4 bg-gray-200 rounded w-80" />
                                <div className="h-4 bg-gray-200 rounded w-72" />
                            </div>
                            <div className="px-7 py-5 space-y-3">
                                <div className="h-4 bg-gray-200 rounded w-40" />
                                <div className="h-3 bg-gray-200 rounded w-64" />
                                <div className="h-10 bg-gray-200 rounded w-full" />
                            </div>
                        </div>
                    </div>

                    { /* Right Column Skeleton */ }
                    <div className="w-80 space-y-5">
                        { /* Apply For Roles Skeleton */ }
                        <div className="bg-white rounded-lg shadow p-7 space-y-4">
                            <div className="h-5 bg-gray-200 rounded w-32" />
                            { [ 1, 2, 3, 4, 5, 6 ].map( ( i ) => (
                                <div
                                    key={ i }
                                    className="flex items-center gap-2"
                                >
                                    <div className="h-[18px] w-[18px] bg-gray-200 rounded" />
                                    <div className="h-4 bg-gray-200 rounded w-28" />
                                </div>
                            ) ) }
                        </div>

                        { /* Rule Priority Skeleton */ }
                        <div className="bg-white rounded-lg shadow p-7 space-y-4">
                            <div className="h-5 bg-gray-200 rounded w-28" />
                            <div className="h-10 bg-gray-200 rounded w-full" />
                        </div>
                    </div>
                </div>
            ) }

            { ! isLoading && (
                <div className="flex gap-5">
                    { /* Left Column */ }
                    <div className="flex-1">
                        <RuleTitle
                            rule={ rule }
                            setRule={ setRule }
                            isSubmitting={ isSubmitting }
                        />

                        { /* Rule Setting */ }
                        <div className="bg-white rounded-lg shadow mb-5">
                            <div className="px-7 py-5 border-b border-gray-200">
                                <h2 className="text-lg font-bold text-[#25252D]">
                                    { __( 'Rule Setting', 'dokan' ) }
                                </h2>
                            </div>

                            { /* Apply on All Products */ }
                            <div className="px-7 py-5 border-b border-gray-200">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-2">
                                        <div className="text-sm font-semibold text-[#25252D]">
                                            { __(
                                                'Apply on All Products',
                                                'dokan'
                                            ) }
                                        </div>
                                        <p className="text-xs text-[#575757]">
                                            { __(
                                                'Apply this rule to all products in your catalog.',
                                                'dokan'
                                            ) }
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        checked={
                                            rule.apply_on_all_product === 1
                                        }
                                        onChange={ ( checked ) => {
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                apply_on_all_product: checked
                                                    ? 1
                                                    : 0,
                                            } ) );
                                            setShowProductsDropdown(
                                                ! checked
                                            );
                                        } }
                                    />
                                </div>
                            </div>

                            { showProductsDropdown && (
                                <>
                                    { /* Specific Products */ }
                                    <div className="px-7 py-5 border-b border-gray-200 space-y-3.5">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-2">
                                                <div className="text-sm font-semibold text-[#25252D]">
                                                    { __(
                                                        'Specific Products',
                                                        'dokan'
                                                    ) }
                                                </div>
                                                <p className="text-xs text-[#575757]">
                                                    { __(
                                                        'Apply this rule to a selection of products.',
                                                        'dokan'
                                                    ) }
                                                </p>
                                            </div>
                                            <ToggleSwitch
                                                checked={
                                                    rule.switches.product_switch
                                                }
                                                onChange={ ( checked ) =>
                                                    setRule( ( prev ) => ( {
                                                        ...prev,
                                                        switches: {
                                                            ...prev.switches,
                                                            product_switch:
                                                                checked,
                                                        },
                                                    } ) )
                                                }
                                            />
                                        </div>

                                        { rule.switches.product_switch && (
                                            <div className="space-y-5">
                                                <div className="quote-rule-included-product-selection-section">
                                                    <div className="block text-sm text-[#575757] mb-1.5">
                                                        { __(
                                                            'Include Products',
                                                            'dokan'
                                                        ) }
                                                    </div>
                                                    <ProductAsyncSelect
                                                        value={ null }
                                                        onChange={
                                                            handleProductSelect
                                                        }
                                                        placeholder={ __(
                                                            'Search product',
                                                            'dokan'
                                                        ) }
                                                        isClearable
                                                        defaultOptions
                                                        loadOptions={
                                                            loadIncludeProductOptions
                                                        }
                                                        filterOption={ (
                                                            option
                                                        ) => {
                                                            const excludeIds =
                                                                new Set(
                                                                    rule.exclude_product_ids.map(
                                                                        ( p ) =>
                                                                            String(
                                                                                p.id
                                                                            )
                                                                    )
                                                                );
                                                            const selfIds =
                                                                new Set(
                                                                    rule.product_ids.map(
                                                                        ( p ) =>
                                                                            String(
                                                                                p.id
                                                                            )
                                                                    )
                                                                );
                                                            return (
                                                                ! excludeIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                ) &&
                                                                ! selfIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                )
                                                            );
                                                        } }
                                                    />
                                                    { rule.product_ids.length >
                                                        0 && (
                                                        <div className="mt-2 space-y-2">
                                                            { rule.product_ids.map(
                                                                (
                                                                    product,
                                                                    idx
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="flex items-center justify-between px-3.5 py-2 bg-[#E4E4EB] rounded border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center gap-3.5">
                                                                            { product?.image && (
                                                                                <div className="w-6 h-6 rounded overflow-hidden border border-[#E9E9E9]">
                                                                                    <img
                                                                                        src={
                                                                                            product.image
                                                                                        }
                                                                                        alt={
                                                                                            product.name
                                                                                        }
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                </div>
                                                                            ) }
                                                                            <span className="text-sm text-[#828282]">
                                                                                {
                                                                                    product.name
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            onClick={ () =>
                                                                                setRule(
                                                                                    (
                                                                                        prev
                                                                                    ) => ( {
                                                                                        ...prev,
                                                                                        product_ids:
                                                                                            prev.product_ids.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    i
                                                                                                ) =>
                                                                                                    i !==
                                                                                                    idx
                                                                                            ),
                                                                                    } )
                                                                                )
                                                                            }
                                                                            className="text-[#828282]"
                                                                        >
                                                                            <X
                                                                                size={
                                                                                    20
                                                                                }
                                                                                color={
                                                                                    '#828282'
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                )
                                                            ) }
                                                        </div>
                                                    ) }
                                                </div>

                                                <div className="quote-rule-excluded-product-selection-section">
                                                    <div className="block text-sm text-[#575757] mb-1.5">
                                                        { __(
                                                            'Exclude Products',
                                                            'dokan'
                                                        ) }
                                                    </div>
                                                    <ProductAsyncSelect
                                                        value={ null }
                                                        onChange={
                                                            handleExcludeProductSelect
                                                        }
                                                        placeholder={ __(
                                                            'Search product',
                                                            'dokan'
                                                        ) }
                                                        isClearable
                                                        defaultOptions
                                                        loadOptions={
                                                            loadExcludeProductOptions
                                                        }
                                                        filterOption={ (
                                                            option
                                                        ) => {
                                                            const includeIds =
                                                                new Set(
                                                                    rule.product_ids.map(
                                                                        ( p ) =>
                                                                            String(
                                                                                p.id
                                                                            )
                                                                    )
                                                                );
                                                            const selfIds =
                                                                new Set(
                                                                    rule.exclude_product_ids.map(
                                                                        ( p ) =>
                                                                            String(
                                                                                p.id
                                                                            )
                                                                    )
                                                                );
                                                            return (
                                                                ! includeIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                ) &&
                                                                ! selfIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                )
                                                            );
                                                        } }
                                                    />
                                                    { rule.exclude_product_ids
                                                        .length > 0 && (
                                                        <div className="mt-2 space-y-2">
                                                            { rule.exclude_product_ids.map(
                                                                (
                                                                    product,
                                                                    idx
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="flex items-center justify-between px-3.5 py-2 bg-[#E4E4EB] rounded border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center gap-3.5">
                                                                            { product?.image && (
                                                                                <div className="w-6 h-6 rounded overflow-hidden border border-[#E9E9E9]">
                                                                                    <img
                                                                                        src={
                                                                                            product.image
                                                                                        }
                                                                                        alt={
                                                                                            product.name
                                                                                        }
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                </div>
                                                                            ) }
                                                                            <span className="text-sm text-[#828282]">
                                                                                {
                                                                                    product.name
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            onClick={ () =>
                                                                                setRule(
                                                                                    (
                                                                                        prev
                                                                                    ) => ( {
                                                                                        ...prev,
                                                                                        exclude_product_ids:
                                                                                            prev.exclude_product_ids.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    i
                                                                                                ) =>
                                                                                                    i !==
                                                                                                    idx
                                                                                            ),
                                                                                    } )
                                                                                )
                                                                            }
                                                                            className="text-[#828282]"
                                                                        >
                                                                            <X
                                                                                size={
                                                                                    20
                                                                                }
                                                                                color={
                                                                                    '#828282'
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                )
                                                            ) }
                                                        </div>
                                                    ) }
                                                </div>
                                            </div>
                                        ) }
                                    </div>

                                    { /* Specific Categories */ }
                                    <div className="px-7 py-5 border-b border-gray-200">
                                        <div
                                            className={ twMerge(
                                                'flex items-center justify-between',
                                                rule.switches.category_switch &&
                                                    'mb-4'
                                            ) }
                                        >
                                            <div className="space-y-2">
                                                <div className="text-sm font-semibold text-[#25252D]">
                                                    { __(
                                                        'Specific Categories',
                                                        'dokan'
                                                    ) }
                                                </div>
                                                <p className="text-xs text-[#575757]">
                                                    { __(
                                                        'Apply this rule to products in select categories.',
                                                        'dokan'
                                                    ) }
                                                </p>
                                            </div>
                                            <ToggleSwitch
                                                checked={
                                                    rule.switches
                                                        .category_switch
                                                }
                                                onChange={ ( checked ) =>
                                                    setRule( ( prev ) => ( {
                                                        ...prev,
                                                        switches: {
                                                            ...prev.switches,
                                                            category_switch:
                                                                checked,
                                                        },
                                                    } ) )
                                                }
                                            />
                                        </div>

                                        { rule.switches.category_switch && (
                                            <div className="max-h-96 overflow-y-auto space-y-5">
                                                { renderCategoryTree(
                                                    categories
                                                ) }
                                            </div>
                                        ) }
                                    </div>

                                    { /* Specific Vendors */ }
                                    <div className="px-7 py-5 border-b border-gray-200 space-y-3.5">
                                        <div className="flex items-center justify-between">
                                            <div className="space-y-2">
                                                <div className="text-sm font-semibold text-[#25252D]">
                                                    { __(
                                                        'Specific Vendors',
                                                        'dokan'
                                                    ) }
                                                </div>
                                                <p className="text-xs text-[#575757]">
                                                    { __(
                                                        'Apply this rule to products from specific vendors.',
                                                        'dokan'
                                                    ) }
                                                </p>
                                            </div>
                                            <ToggleSwitch
                                                checked={
                                                    rule.switches.vendor_switch
                                                }
                                                onChange={ ( checked ) =>
                                                    setRule( ( prev ) => ( {
                                                        ...prev,
                                                        switches: {
                                                            ...prev.switches,
                                                            vendor_switch:
                                                                checked,
                                                        },
                                                    } ) )
                                                }
                                            />
                                        </div>

                                        { rule.switches.vendor_switch && (
                                            <div className="space-y-5">
                                                <div className="quote-rule-included-vendor-selection-section">
                                                    <div className="block text-sm text-[#575757] mb-1.5">
                                                        { __(
                                                            'Include Vendors',
                                                            'dokan'
                                                        ) }
                                                    </div>
                                                    <VendorAsyncSelect
                                                        value={ null }
                                                        onChange={
                                                            handleVendorSelect
                                                        }
                                                        placeholder={ __(
                                                            'Type to search',
                                                            'dokan'
                                                        ) }
                                                        isClearable
                                                        defaultOptions
                                                        filterOption={ (
                                                            option
                                                        ) => {
                                                            const excludeIds =
                                                                new Set(
                                                                    rule.exclude_store_ids.map(
                                                                        ( s ) =>
                                                                            String(
                                                                                s.id
                                                                            )
                                                                    )
                                                                );
                                                            const selfIds =
                                                                new Set(
                                                                    rule.store_ids.map(
                                                                        ( s ) =>
                                                                            String(
                                                                                s.id
                                                                            )
                                                                    )
                                                                );
                                                            return (
                                                                ! excludeIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                ) &&
                                                                ! selfIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                )
                                                            );
                                                        } }
                                                    />
                                                    { rule.store_ids.length >
                                                        0 && (
                                                        <div className="mt-2 space-y-2">
                                                            { rule.store_ids.map(
                                                                (
                                                                    vendor,
                                                                    idx
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="flex items-center justify-between px-3.5 py-2 bg-[#E4E4EB] rounded border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center gap-3.5">
                                                                            { vendor?.image && (
                                                                                <div className="w-6 h-6 rounded overflow-hidden border border-[#E9E9E9]">
                                                                                    <img
                                                                                        src={
                                                                                            vendor.image
                                                                                        }
                                                                                        alt={
                                                                                            vendor.name
                                                                                        }
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                </div>
                                                                            ) }
                                                                            <span className="text-sm text-[#828282]">
                                                                                {
                                                                                    vendor.name
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            onClick={ () =>
                                                                                setRule(
                                                                                    (
                                                                                        prev
                                                                                    ) => ( {
                                                                                        ...prev,
                                                                                        store_ids:
                                                                                            prev.store_ids.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    i
                                                                                                ) =>
                                                                                                    i !==
                                                                                                    idx
                                                                                            ),
                                                                                    } )
                                                                                )
                                                                            }
                                                                            className="text-[#828282]"
                                                                        >
                                                                            <X
                                                                                size={
                                                                                    20
                                                                                }
                                                                                color={
                                                                                    '#828282'
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                )
                                                            ) }
                                                        </div>
                                                    ) }
                                                </div>

                                                <div className="quote-rule-excluded-vendor-selection-section">
                                                    <div className="block text-sm text-[#575757] mb-1.5">
                                                        { __(
                                                            'Exclude Vendors',
                                                            'dokan'
                                                        ) }
                                                    </div>
                                                    <VendorAsyncSelect
                                                        value={ null }
                                                        onChange={
                                                            handleExcludeVendorSelect
                                                        }
                                                        placeholder={ __(
                                                            'Type to search',
                                                            'dokan'
                                                        ) }
                                                        isClearable
                                                        defaultOptions
                                                        filterOption={ (
                                                            option
                                                        ) => {
                                                            const includeIds =
                                                                new Set(
                                                                    rule.store_ids.map(
                                                                        ( s ) =>
                                                                            String(
                                                                                s.id
                                                                            )
                                                                    )
                                                                );
                                                            const selfIds =
                                                                new Set(
                                                                    rule.exclude_store_ids.map(
                                                                        ( s ) =>
                                                                            String(
                                                                                s.id
                                                                            )
                                                                    )
                                                                );
                                                            return (
                                                                ! includeIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                ) &&
                                                                ! selfIds.has(
                                                                    String(
                                                                        option.value
                                                                    )
                                                                )
                                                            );
                                                        } }
                                                    />
                                                    { rule.exclude_store_ids
                                                        .length > 0 && (
                                                        <div className="mt-2 space-y-2">
                                                            { rule.exclude_store_ids.map(
                                                                (
                                                                    vendor,
                                                                    idx
                                                                ) => (
                                                                    <div
                                                                        key={
                                                                            idx
                                                                        }
                                                                        className="flex items-center justify-between px-3.5 py-2 bg-[#E4E4EB] rounded border border-gray-200"
                                                                    >
                                                                        <div className="flex items-center gap-3.5">
                                                                            { vendor?.image && (
                                                                                <div className="w-6 h-6 rounded overflow-hidden border border-[#E9E9E9]">
                                                                                    <img
                                                                                        src={
                                                                                            vendor.image
                                                                                        }
                                                                                        alt={
                                                                                            vendor.name
                                                                                        }
                                                                                        className="w-full h-full object-cover"
                                                                                    />
                                                                                </div>
                                                                            ) }
                                                                            <span className="text-sm text-[#828282]">
                                                                                {
                                                                                    vendor.name
                                                                                }
                                                                            </span>
                                                                        </div>
                                                                        <button
                                                                            onClick={ () =>
                                                                                setRule(
                                                                                    (
                                                                                        prev
                                                                                    ) => ( {
                                                                                        ...prev,
                                                                                        exclude_store_ids:
                                                                                            prev.exclude_store_ids.filter(
                                                                                                (
                                                                                                    _,
                                                                                                    i
                                                                                                ) =>
                                                                                                    i !==
                                                                                                    idx
                                                                                            ),
                                                                                    } )
                                                                                )
                                                                            }
                                                                            className="text-[#828282]"
                                                                        >
                                                                            <X
                                                                                size={
                                                                                    20
                                                                                }
                                                                                color={
                                                                                    '#828282'
                                                                                }
                                                                            />
                                                                        </button>
                                                                    </div>
                                                                )
                                                            ) }
                                                        </div>
                                                    ) }
                                                </div>
                                            </div>
                                        ) }
                                    </div>
                                </>
                            ) }

                            { /* Expire Limit */ }
                            <div className="px-7 py-5 border-b border-gray-200 space-y-3.5">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-2">
                                        <div className="text-sm font-semibold text-[#25252D]">
                                            { __( 'Expire Limit', 'dokan' ) }
                                        </div>
                                        <p className="text-xs text-[#575757]">
                                            { __(
                                                'Quote remains valid for a set number of days after approval.',
                                                'dokan'
                                            ) }
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        checked={ rule.switches.expire_switch }
                                        onChange={ ( checked ) =>
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                switches: {
                                                    ...prev.switches,
                                                    expire_switch: checked,
                                                },
                                            } ) )
                                        }
                                    />
                                </div>

                                { rule.switches.expire_switch && (
                                    <SimpleInput
                                        addOnRight={ __( 'Days', 'dokan' ) }
                                        className="focus:outline-0"
                                        onChange={ ( e ) =>
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                expire_limit:
                                                    parseInt(
                                                        e.target.value
                                                    ) || 0,
                                            } ) )
                                        }
                                        input={ {
                                            type: 'number',
                                            value: rule.expire_limit,
                                            placeholder: __(
                                                'Type expiration day',
                                                'dokan'
                                            ),
                                            min: 1,
                                        } }
                                    />
                                ) }
                            </div>

                            { /* Hide Price */ }
                            <div className="px-7 py-5 space-y-3.5">
                                <div className="flex items-center justify-between">
                                    <div className="space-y-2">
                                        <div className="text-sm font-semibold text-[#25252D]">
                                            { __( 'Hide Price', 'dokan' ) }
                                        </div>
                                        <p className="text-xs text-[#575757]">
                                            { __(
                                                'Hide the price of products when this rule applies.',
                                                'dokan'
                                            ) }
                                        </p>
                                    </div>
                                    <ToggleSwitch
                                        checked={ rule.hide_price === 1 }
                                        onChange={ ( checked ) => {
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                hide_price: checked ? 1 : 0,
                                                hide_cart_button: checked
                                                    ? 'replace'
                                                    : prev.hide_cart_button,
                                            } ) );
                                        } }
                                    />
                                </div>

                                { rule.hide_price === 1 && (
                                    <SimpleInput
                                        className="focus:outline-0 px-4"
                                        onChange={ ( e ) =>
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                hide_price_text: e.target.value,
                                            } ) )
                                        }
                                        input={ {
                                            type: 'text',
                                            value: rule.hide_price_text,
                                            placeholder: __(
                                                'Price is hidden',
                                                'dokan'
                                            ),
                                        } }
                                    />
                                ) }
                            </div>
                        </div>

                        { /* Add to Cart Button Customization */ }
                        <div className="bg-white rounded-lg shadow">
                            <div className="px-7 py-5 border-b border-gray-200">
                                <h2 className="text-lg font-bold text-[#25252D]">
                                    { __(
                                        'Add to Cart Button Customization',
                                        'dokan'
                                    ) }
                                </h2>
                            </div>

                            <div>
                                { /* Add to Cart Button Visibility */ }
                                <div className="border-b border-gray-200">
                                    <div className="px-7 py-5 space-y-3.5">
                                        <label
                                            htmlFor="quote-rule-hide-cart-button"
                                            className="text-sm font-semibold text-[#25252D]"
                                        >
                                            { __(
                                                'Add to Cart Button Visibility',
                                                'dokan'
                                            ) }
                                        </label>
                                        <SimpleRadio
                                            name="hide_cart_button"
                                            options={ [
                                                {
                                                    label: __(
                                                        'Replace Add to Cart button with a Quote Button',
                                                        'dokan'
                                                    ),
                                                    value: 'replace',
                                                },
                                                {
                                                    label: __(
                                                        'Keep Add to Cart button and add a new Quote Button',
                                                        'dokan'
                                                    ),
                                                    value: 'keep_and_add_new',
                                                },
                                            ] }
                                            value={ rule.hide_cart_button }
                                            onChange={ ( e ) => {
                                                setRule( ( prevState ) => {
                                                    return {
                                                        ...prevState,
                                                        hide_cart_button:
                                                            e.target.value,
                                                    };
                                                } );
                                            } }
                                            // disabled={ rule.hide_price === 1 }
                                            input={ {
                                                type: 'radio',
                                                id: 'quote-rule-hide-cart-button',
                                            } }
                                            optionClass="p-0 first:h-8 block text-[#575757] text-sm !font-normal"
                                        />
                                    </div>
                                </div>

                                { /* Custom Button Label */ }
                                <div className="px-7 py-5 space-y-3.5">
                                    <div className="space-y-2">
                                        <div className="block text-sm font-semibold text-[#25252D]">
                                            { __(
                                                'Custom Button Label',
                                                'dokan'
                                            ) }
                                        </div>
                                        <p className="text-xs text-[#575757]">
                                            { __(
                                                'Display the above label on custom button, e.g "Request a Quote"',
                                                'dokan'
                                            ) }
                                        </p>
                                    </div>
                                    <SimpleInput
                                        className="focus:outline-0 px-4"
                                        onChange={ ( e ) =>
                                            setRule( ( prev ) => ( {
                                                ...prev,
                                                button_text: e.target.value,
                                            } ) )
                                        }
                                        input={ {
                                            type: 'text',
                                            value: rule.button_text,
                                            placeholder: __(
                                                'Add to quote',
                                                'dokan'
                                            ),
                                        } }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    { /* Right Column */ }
                    <div className="w-80">
                        <ApplyForRoles
                            allRoles={ allRoles }
                            rule={ rule }
                            handleRoleChange={ handleRoleChange }
                        />

                        <RulePriority rule={ rule } setRule={ setRule } />
                    </div>
                </div>
            ) }
        </>
    );
};

export default AddNewQuoteRule;
